import React from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { Form, Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import { parseFloat } from 'modules/main/formaters';
import {
  Modal,
  Button,
  DragAndDrop,
  TextInput,
  ModalFooter,
  Tooltip,
} from 'modules/ui/components';
import { useCreateOrUpdateBoat } from 'modules/boats';
import { UserSelectors } from 'modules/user';
import { Plan, PLAN_HIERARCHY } from 'modules/subscription';
import { settingsDefaultsWithDefaultValuesSelector } from 'modules/settings';
import { SettingsDefaults } from 'modules/settings/types';
import { LocationApiModule } from 'modules/api';
import {
  validateRequiredString,
  validateRequiredNumber,
  validateCorrectionPercentage,
  validateSpeedLimit,
  validateWaveHeightLimit,
} from 'modules/form';

import {
  FormContainer,
  DragAndDropContainer,
  BoatSelectRow,
  BoatSelect,
  MultiRow,
} from './styledComponents';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
};

const SettingsModal: React.FC<Props> = ({ isOpen, closeModal }) => {
  const userPlanTier = useSelector(UserSelectors.userPlanTier);
  const pricingEnabled = process.env.REACT_APP_STRIPE_ENABLED === 'true';
  const canManageMultipleBoats =
    pricingEnabled && PLAN_HIERARCHY[userPlanTier] >= PLAN_HIERARCHY[Plan.Pro];

  const defaults = useSelector(settingsDefaultsWithDefaultValuesSelector);

  const {
    boat,
    boats,
    selectedBoatId,
    isCreatingNew,
    selectBoat,
    startCreatingNew,
    boatExists,
    handleUpdateOrCreate,
    setPolarFile,
    setSailsFile,
    setWaveFile,
    removeSailsFile,
    removeWaveFile,
    removeSails,
    removeWave,
    initialName,
    polarsFile,
    sailsFile,
    waveFile,
    polarsValidation,
    sailsValidation,
    waveValidation,
    hasValidationErrors,
    isValidating,
  } = useCreateOrUpdateBoat(closeModal);

  const savedFileToFile = (
    saved?: { fileName: string; timeStamp: string } | null,
  ): File | null =>
    saved
      ? new File([], saved.fileName, {
          lastModified: new Date(saved.timeStamp).getTime(),
        })
      : null;

  // The file shown in each field: a newly picked file, otherwise the saved
  // file unless it has been marked for removal.
  const polarDisplayFile = polarsFile || savedFileToFile(boat?.polarsFile);
  const sailsDisplayFile =
    sailsFile || (removeSails ? null : savedFileToFile(boat?.sailsFile));
  const waveDisplayFile =
    waveFile || (removeWave ? null : savedFileToFile(boat?.waveFile));

  // Seed the Config tab from the boat's saved config when editing, otherwise
  // from the global defaults. speedLimit always comes from the cleaned global
  // default value.
  const configDefaults: SettingsDefaults = {
    ...defaults,
    ...boat?.config,
    speedLimit: defaults.speedLimit,
  };

  const onSubmit = async ({
    name,
    defaults: formDefaults,
  }: {
    name: string;
    defaults?: SettingsDefaults;
  }): Promise<void> => {
    await handleUpdateOrCreate({ name, config: formDefaults });
  };

  return (
    <Modal
      isOpen={isOpen}
      width={712}
      title={'Boat configuration'}
      closeModal={closeModal}
      onRequestClose={closeModal}
    >
      <Form
        onSubmit={onSubmit}
        initialValues={{ name: initialName, defaults: configDefaults }}
        render={({
          handleSubmit,
          invalid,
          submitting,
          values,
        }): React.ReactNode => (
          <>
            <Tabs>
              <TabList>
                <Tab>Files</Tab>
                <Tab>Settings</Tab>
              </TabList>
              <TabPanel forceRender>
                <FormContainer>
                  {canManageMultipleBoats && boats.length > 0 && (
                    <BoatSelectRow>
                      <BoatSelect
                        value={isCreatingNew ? '' : selectedBoatId ?? ''}
                        onChange={(e): void =>
                          selectBoat(Number(e.target.value))
                        }
                      >
                        {isCreatingNew && (
                          <option value="" disabled>
                            New boat
                          </option>
                        )}
                        {boats.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                      </BoatSelect>
                      <Button
                        isSecondary
                        label="Add boat"
                        clickHandler={startCreatingNew}
                        disabled={isCreatingNew}
                      />
                    </BoatSelectRow>
                  )}
                  <Field
                    name="name"
                    label="Boat name *"
                    type="text"
                    component={TextInput}
                    validate={validateRequiredString}
                  />
                  <DragAndDropContainer>
                    <DragAndDrop
                      file={polarDisplayFile}
                      label="Polar *"
                      onChange={setPolarFile}
                      validationStatus={polarsValidation.status}
                      validationError={polarsValidation.error}
                    />
                  </DragAndDropContainer>
                  <DragAndDropContainer>
                    <DragAndDrop
                      file={sailsDisplayFile}
                      label="Sail configuration table"
                      onChange={setSailsFile}
                      onRemove={sailsDisplayFile ? removeSailsFile : undefined}
                      validationStatus={sailsValidation.status}
                      validationError={sailsValidation.error}
                    />
                  </DragAndDropContainer>

                  <DragAndDropContainer>
                    <DragAndDrop
                      file={waveDisplayFile}
                      label="Wave correction table"
                      onChange={setWaveFile}
                      onRemove={waveDisplayFile ? removeWaveFile : undefined}
                      validationStatus={waveValidation.status}
                      validationError={waveValidation.error}
                    />
                  </DragAndDropContainer>
                </FormContainer>
              </TabPanel>
              <TabPanel forceRender>
                <FormContainer>
                  <MultiRow>
                    <Field
                      name="defaults.polarEfficiency"
                      parse={parseFloat}
                      component={TextInput}
                      label="Polar efficiency"
                      rightInlineLabel="%"
                      width={133}
                      validate={validateCorrectionPercentage}
                    />
                    <Field
                      name="defaults.waveCorrection"
                      parse={parseFloat}
                      component={TextInput}
                      label="Wave correction factor"
                      rightInlineLabel="%"
                      width={133}
                      validate={validateCorrectionPercentage}
                    />
                    <Field
                      name="defaults.speedLimit"
                      parse={parseFloat}
                      component={TextInput}
                      label="Wind speed limit"
                      rightInlineLabel="kts"
                      width={133}
                      validate={validateSpeedLimit}
                    />
                  </MultiRow>
                  <MultiRow>
                    <Field
                      name="defaults.sailChangePenalty"
                      parse={parseFloat}
                      component={TextInput}
                      label="Sail change penalty"
                      rightInlineLabel="min"
                      width={133}
                      validate={validateRequiredNumber}
                    />
                    <Field
                      name="defaults.tackPenalty"
                      parse={parseFloat}
                      component={TextInput}
                      label="Tack penalty"
                      rightInlineLabel="min"
                      width={133}
                      validate={validateRequiredNumber}
                    />
                    <Field
                      name="defaults.gybePenalty"
                      parse={parseFloat}
                      component={TextInput}
                      label="Gybe penalty"
                      rightInlineLabel="min"
                      width={133}
                      validate={validateRequiredNumber}
                    />
                  </MultiRow>
                  <MultiRow>
                    <Field
                      name="defaults.waveHeightLimit"
                      parse={parseFloat}
                      component={TextInput}
                      label="Wave height limit"
                      rightInlineLabel="m"
                      width={133}
                      validate={validateWaveHeightLimit}
                    />
                    <Field
                      name="defaults.url"
                      type="text"
                      component={TextInput}
                      label="URL for live boat data"
                      width={400}
                    />
                    {values?.defaults?.url ===
                      LocationApiModule.getCurrentLocation && (
                      <div style={{ marginTop: 16, marginLeft: -16 }}>
                        <Tooltip
                          multiline
                          text="This is just a temporary setting that always returns the boat position at Les Sables-d’Olonne.<br> Reach out to us via email (contact@doradosail.com) to set you up with live boat data."
                        />
                      </div>
                    )}
                  </MultiRow>
                </FormContainer>
              </TabPanel>
            </Tabs>
            <ModalFooter>
              <Button isSecondary label="Cancel" clickHandler={closeModal} />
              <Button
                isLoading={submitting}
                disabled={
                  invalid ||
                  (!boatExists && !polarsFile) ||
                  submitting ||
                  hasValidationErrors ||
                  isValidating
                }
                label={boatExists ? 'Update' : 'Create'}
                clickHandler={handleSubmit}
              />
            </ModalFooter>
          </>
        )}
      />
    </Modal>
  );
};

export default React.memo(SettingsModal);
